<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { useTimelinePointerToSeconds } from '@/animation/interaction/useTimelinePointerToSeconds'
import { snapPlayheadForUi } from '@/animation/timeline/playheadSnap'
import { deepCloneLayer } from '@/editor/history/cloneUtils'
import type { AnimationClip } from '@/types/animationClip'
import type { AnimationLayer } from '@/types/animationLayer'
import { useMockupEditorStore } from '@/stores/mockupEditor'

const store = useMockupEditorStore()
const {
  animationLayers,
  currentTimeSeconds,
  timelineMaxSeconds,
  timelineTrackWidthFactor,
  timelineRulerTickSet,
  selectedClipId,
  selectedLayerId,
  selectedKeyframeId,
} = storeToRefs(store)

const rulerScrollHostRef = ref<HTMLElement | null>(null)
const trackScrollHostRef = ref<HTMLElement | null>(null)
/** 与刻度、clip、播放头 `left:%` 共用同一宽度盒（无水平 padding），保证 0s 与 `left:0%` 对齐 */
const trackWideRef = ref<HTMLElement | null>(null)
const { eventToSeconds } = useTimelinePointerToSeconds(trackScrollHostRef, () => timelineMaxSeconds.value, {
  contentRef: trackWideRef,
})

const trackInnerStyle = computed(() => ({
  width: `${Math.max(100, timelineTrackWidthFactor.value * 100)}%`,
}))

let trackResizeObserver: ResizeObserver | null = null
let syncingHorizontalScroll = false

function syncHorizontalScroll(source: 'ruler' | 'track'): void {
  if (syncingHorizontalScroll) return
  const rulerEl = rulerScrollHostRef.value
  const trackEl = trackScrollHostRef.value
  if (!rulerEl || !trackEl) return
  syncingHorizontalScroll = true
  if (source === 'ruler') trackEl.scrollLeft = rulerEl.scrollLeft
  else rulerEl.scrollLeft = trackEl.scrollLeft
  requestAnimationFrame(() => {
    syncingHorizontalScroll = false
  })
}

function onRulerScroll(): void {
  syncHorizontalScroll('ruler')
}

function onTrackScroll(): void {
  syncHorizontalScroll('track')
}

onMounted(() => {
  void nextTick(() => {
    const el = trackScrollHostRef.value
    if (!el) return
    const report = (): void => {
      store.setTimelineTrackClientWidth(el.clientWidth)
    }
    report()
    trackResizeObserver = new ResizeObserver(() => report())
    trackResizeObserver.observe(el)
  })
})

onBeforeUnmount(() => {
  trackResizeObserver?.disconnect()
  trackResizeObserver = null
  syncingHorizontalScroll = false
  endWindowDrag()
  endPlayheadDragListeners()
  endVisibilityWindowDrag()
})

/**
 * @param tSec - 时刻（秒）
 */
function tickLabel(tSec: number): string {
  return `${Math.round(Number(tSec))}s`
}

function clipLeftPct(clip: AnimationClip): string {
  const max = timelineMaxSeconds.value
  if (max <= 0) return '0%'
  return `${(clip.startSec / max) * 100}%`
}

function clipWidthPct(clip: AnimationClip): string {
  const max = timelineMaxSeconds.value
  if (max <= 0) return '0%'
  return `${(clip.durationSec / max) * 100}%`
}

const playheadLeftPct = computed(() => {
  const max = timelineMaxSeconds.value
  if (max <= 0) return '0%'
  return `${(currentTimeSeconds.value / max) * 100}%`
})

const KEY_STEP_SEC = 0.05
const PLAYHEAD_GRID_STEP_SEC = 0

/**
 * 指针位置换算时间并写入 store（磁吸 + 可选网格）。
 */
function applySeekFromEvent(ev: PointerEvent): void {
  const raw = eventToSeconds(ev)
  const t = snapPlayheadForUi(raw, animationLayers.value, timelineMaxSeconds.value, PLAYHEAD_GRID_STEP_SEC)
  store.setCurrentTime(t)
}

let playheadDragActive = false

function endPlayheadDragListeners(): void {
  if (!playheadDragActive) return
  playheadDragActive = false
  window.removeEventListener('pointermove', onPlayheadWindowMove)
  window.removeEventListener('pointerup', endPlayheadDragListeners)
  window.removeEventListener('pointercancel', endPlayheadDragListeners)
}

function onPlayheadWindowMove(ev: PointerEvent): void {
  applySeekFromEvent(ev)
}

function beginPlayheadDragListeners(): void {
  endWindowDrag()
  endPlayheadDragListeners()
  playheadDragActive = true
  window.addEventListener('pointermove', onPlayheadWindowMove)
  window.addEventListener('pointerup', endPlayheadDragListeners)
  window.addEventListener('pointercancel', endPlayheadDragListeners)
}

function onRulerPointerDown(ev: PointerEvent): void {
  applySeekFromEvent(ev)
  beginPlayheadDragListeners()
}

/**
 * @param ev - 指针事件
 * @param layerId - 被点击的轨道对应图层 id（用于与左侧列表选中态一致）
 */
function onLaneBackgroundPointerDown(ev: PointerEvent, layerId: string): void {
  if ((ev.target as HTMLElement).closest('.clipBlock')) return
  store.selectAnimationLayer(layerId)
  store.selectAnimationClip(null)
  applySeekFromEvent(ev)
  beginPlayheadDragListeners()
}

function onEmptyLanePointerDown(ev: PointerEvent): void {
  applySeekFromEvent(ev)
  beginPlayheadDragListeners()
}

function onPlayheadKnobPointerDown(ev: PointerEvent): void {
  ev.stopPropagation()
  ev.preventDefault()
  applySeekFromEvent(ev)
  beginPlayheadDragListeners()
}

/**
 * 键盘微调播放头。
 */
function onTimelineKeydown(ev: KeyboardEvent): void {
  const max = timelineMaxSeconds.value
  const cur = currentTimeSeconds.value
  let next: number | undefined
  if (ev.key === 'ArrowLeft' || ev.key === 'ArrowDown') next = cur - KEY_STEP_SEC
  else if (ev.key === 'ArrowRight' || ev.key === 'ArrowUp') next = cur + KEY_STEP_SEC
  else if (ev.key === 'Home') next = 0
  else if (ev.key === 'End') next = max
  if (next === undefined) return
  ev.preventDefault()
  const t = snapPlayheadForUi(next, animationLayers.value, max, PLAYHEAD_GRID_STEP_SEC)
  store.setCurrentTime(t)
}

type DragKind = 'move' | 'left' | 'right' | null
type VisibilityDragKind = 'move' | 'start' | 'end' | null

const dragState = ref<{
  kind: DragKind
  layerId: string
  clipId: string
  startClipStart: number
  startClipDuration: number
  anchorSec: number
  /** 拖拽开始前该图层的深拷贝（用于撤销一条 clip 几何变更） */
  layerBefore: AnimationLayer
} | null>(null)

const visibilityDragState = ref<{
  kind: VisibilityDragKind
  layerId: string
  anchorSec: number
  startSec: number
  endSec: number
} | null>(null)

function visibilityLeftPct(layer: AnimationLayer): string {
  const max = timelineMaxSeconds.value
  if (max <= 0) return '0%'
  return `${(layer.visibilityStartTimeSec / max) * 100}%`
}

function visibilityWidthPct(layer: AnimationLayer): string {
  const max = timelineMaxSeconds.value
  if (max <= 0) return '0%'
  const width = Math.max(0, layer.visibilityEndTimeSec - layer.visibilityStartTimeSec)
  return `${(width / max) * 100}%`
}

function onWindowPointerMove(ev: PointerEvent): void {
  const d = dragState.value
  if (!d) return
  const cur = eventToSeconds(ev)
  const delta = cur - d.anchorSec
  if (d.kind === 'move') {
    store.commitClipMove(d.layerId, d.clipId, d.startClipStart + delta)
  } else if (d.kind === 'left') {
    store.commitClipResizeLeft(d.layerId, d.clipId, d.startClipStart + delta)
  } else if (d.kind === 'right') {
    store.commitClipResizeRight(d.layerId, d.clipId, d.startClipDuration + delta)
  }
}

function endWindowDrag(): void {
  const d = dragState.value
  if (d) {
    const lyr = animationLayers.value.find((l) => l.id === d.layerId)
    if (lyr) {
      const after = deepCloneLayer(lyr)
      store.recordEditorLayerGestureIfChanged(d.layerId, d.layerBefore, after, 'Clip timing')
    }
  }
  window.removeEventListener('pointermove', onWindowPointerMove)
  window.removeEventListener('pointerup', endWindowDrag)
  window.removeEventListener('pointercancel', endWindowDrag)
  dragState.value = null
}

function onVisibilityWindowPointerMove(ev: PointerEvent): void {
  const d = visibilityDragState.value
  if (!d) return
  const cur = eventToSeconds(ev)
  const delta = cur - d.anchorSec
  const duration = d.endSec - d.startSec
  if (d.kind === 'start') {
    store.setLayerVisibilityWindow(d.layerId, d.startSec + delta, d.endSec)
    return
  }
  if (d.kind === 'end') {
    store.setLayerVisibilityWindow(d.layerId, d.startSec, d.endSec + delta)
    return
  }
  const nextStart = d.startSec + delta
  const nextEnd = nextStart + duration
  store.setLayerVisibilityWindow(d.layerId, nextStart, nextEnd)
}

function endVisibilityWindowDrag(): void {
  window.removeEventListener('pointermove', onVisibilityWindowPointerMove)
  window.removeEventListener('pointerup', endVisibilityWindowDrag)
  window.removeEventListener('pointercancel', endVisibilityWindowDrag)
  visibilityDragState.value = null
}

function onVisibilityWindowPointerDown(
  ev: PointerEvent,
  layerId: string,
  kind: Exclude<VisibilityDragKind, null>,
): void {
  ev.stopPropagation()
  ev.preventDefault()
  endPlayheadDragListeners()
  endWindowDrag()
  if (store.isLayerLocked(layerId)) {
    store.setStubMessage(
      store.tUi('editorMessages.lockedCannot', {
        action: store.tUi('editorActions.editTimelineClip'),
      }),
    )
    return
  }
  const layer = animationLayers.value.find((item) => item.id === layerId)
  if (!layer) return
  store.selectAnimationLayer(layerId)
  const anchor = eventToSeconds(ev)
  visibilityDragState.value = {
    kind,
    layerId,
    anchorSec: anchor,
    startSec: layer.visibilityStartTimeSec,
    endSec: layer.visibilityEndTimeSec,
  }
  window.addEventListener('pointermove', onVisibilityWindowPointerMove)
  window.addEventListener('pointerup', endVisibilityWindowDrag)
  window.addEventListener('pointercancel', endVisibilityWindowDrag)
}

function onClipPointerDown(
  ev: PointerEvent,
  layerId: string,
  clip: AnimationClip,
  kind: Exclude<DragKind, null>,
): void {
  ev.stopPropagation()
  ev.preventDefault()
  endPlayheadDragListeners()
  if (store.isLayerLocked(layerId)) {
    store.setStubMessage(
      store.tUi('editorMessages.lockedCannot', {
        action: store.tUi('editorActions.editTimelineClip'),
      }),
    )
    return
  }
  store.selectAnimationClip(clip.id)
  const lyr = animationLayers.value.find((l) => l.id === layerId)
  if (!lyr) return
  const anchor = eventToSeconds(ev)
  dragState.value = {
    kind,
    layerId,
    clipId: clip.id,
    startClipStart: clip.startSec,
    startClipDuration: clip.durationSec,
    anchorSec: anchor,
    layerBefore: deepCloneLayer(lyr),
  }
  window.addEventListener('pointermove', onWindowPointerMove)
  window.addEventListener('pointerup', endWindowDrag)
  window.addEventListener('pointercancel', endWindowDrag)
}

/**
 * 选中片段边缘关键帧并将播放头对齐到该关键帧。
 *
 * @param clip - 目标片段
 * @param edge - 边缘类型（left 为片段起点，right 为片段终点）
 */
function selectEdgeKeyframe(clip: AnimationClip, edge: 'left' | 'right'): void {
  if (clip.keyframes.length === 0) return
  const targetTimeInClip = edge === 'left' ? 0 : clip.durationSec
  let picked = clip.keyframes[0]!
  let bestDistance = Math.abs(picked.timeInClip - targetTimeInClip)
  for (const kf of clip.keyframes) {
    const d = Math.abs(kf.timeInClip - targetTimeInClip)
    if (d < bestDistance) {
      picked = kf
      bestDistance = d
    }
  }
  store.selectAnimationClip(clip.id)
  store.selectKeyframe(clip.id, picked.id)
  store.setCurrentTime(clip.startSec + picked.timeInClip)
}

/**
 * 点击关键帧后同步片段选中、关键帧选中与播放头时间。
 *
 * @param clip - 关键帧所属片段
 * @param keyframeId - 关键帧 id
 * @param keyframeTimeInClip - 关键帧在片段内时间（秒）
 */
function onKeyframeClick(clip: AnimationClip, keyframeId: string, keyframeTimeInClip: number): void {
  store.selectAnimationClip(clip.id)
  store.selectKeyframe(clip.id, keyframeId)
  store.setCurrentTime(clip.startSec + keyframeTimeInClip)
}
</script>

<template>
  <div
    class="timelineRoot"
    tabindex="0"
    role="group"
    :aria-label="store.tUi('timelineTracks.groupLabel')"
    @keydown="onTimelineKeydown"
  >
    <div class="rulerSticky">
      <div ref="rulerScrollHostRef" class="rulerScrollHost" @scroll="onRulerScroll">
        <div class="rulerWide" :style="trackInnerStyle">
          <div class="rulerRow" @pointerdown="onRulerPointerDown">
            <div class="ticks" aria-hidden="true">
              <span
                v-for="t in timelineRulerTickSet.minorSeconds"
                :key="'minor-' + t"
                class="tick tick--minor"
                :style="{ left: `${(t / timelineMaxSeconds) * 100}%` }"
              />
              <span
                v-for="t in timelineRulerTickSet.majorSeconds"
                :key="'major-' + t"
                class="tick tick--major"
                :style="{ left: `${(t / timelineMaxSeconds) * 100}%` }"
              >
                <span class="lbl">{{ tickLabel(t) }}</span>
              </span>
            </div>
            <div class="playheadHeader" :style="{ left: playheadLeftPct }">
              <button
                type="button"
                class="playheadKnob"
                :aria-label="store.tUi('timelineTracks.playheadAria')"
                @pointerdown="onPlayheadKnobPointerDown"
              />
              <div class="playheadHeaderStem" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div ref="trackScrollHostRef" class="trackScrollHost" @scroll="onTrackScroll">
      <div ref="trackWideRef" class="trackWide" :style="trackInnerStyle">
        <div v-if="animationLayers.length > 0" class="laneStack">
          <div
            v-for="layer in animationLayers"
            :key="layer.id"
            class="laneRow"
            :class="{ selected: layer.id === selectedLayerId }"
            :aria-label="store.tUi('timelineTracks.laneAria', { name: layer.name })"
          >
            <div class="laneInner" @pointerdown="onLaneBackgroundPointerDown($event, layer.id)">
              <div
                class="visibilityWindow"
                :style="{ left: visibilityLeftPct(layer), width: visibilityWidthPct(layer) }"
                @pointerdown="onVisibilityWindowPointerDown($event, layer.id, 'move')"
              >
                <button
                  type="button"
                  class="visibilityHandle visibilityHandle--start"
                  :aria-label="store.tUi('timelineTracks.visibilityStart')"
                  @pointerdown.stop="onVisibilityWindowPointerDown($event, layer.id, 'start')"
                >
                  <span class="visibilityHandleGrip" aria-hidden="true" />
                </button>
                <div class="visibilityBody" />
                <button
                  type="button"
                  class="visibilityHandle visibilityHandle--end"
                  :aria-label="store.tUi('timelineTracks.visibilityEnd')"
                  @pointerdown.stop="onVisibilityWindowPointerDown($event, layer.id, 'end')"
                >
                  <span class="visibilityHandleGrip" aria-hidden="true" />
                </button>
              </div>
              <div
                v-for="clip in layer.clips"
                :key="clip.id"
                class="clipBlock"
                :class="{ selected: clip.id === selectedClipId }"
                :style="{ left: clipLeftPct(clip), width: clipWidthPct(clip) }"
                @pointerdown="onClipPointerDown($event, layer.id, clip, 'move')"
              >
                <button
                  type="button"
                  class="handle handle--left"
                  :aria-label="store.tUi('timelineTracks.trimStart')"
                  @pointerdown.stop="onClipPointerDown($event, layer.id, clip, 'left')"
                  @click.stop="selectEdgeKeyframe(clip, 'left')"
                />
                <div class="clipBody" />
                <div class="clipKeyframes">
                  <button
                    v-for="kf in clip.keyframes"
                    :key="kf.id"
                    type="button"
                    class="kfInClip"
                    :class="{ selected: kf.id === selectedKeyframeId }"
                    :title="store.tUi('timelineTracks.keyframeInClip', { seconds: kf.timeInClip.toFixed(2) })"
                    :style="{ left: `${clip.durationSec > 0 ? (kf.timeInClip / clip.durationSec) * 100 : 0}%` }"
                    @pointerdown.stop
                    @click.stop="onKeyframeClick(clip, kf.id, kf.timeInClip)"
                  />
                </div>
                <button
                  type="button"
                  class="handle handle--right"
                  :aria-label="store.tUi('timelineTracks.trimEnd')"
                  @pointerdown.stop="onClipPointerDown($event, layer.id, clip, 'right')"
                  @click.stop="selectEdgeKeyframe(clip, 'right')"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-else
          class="laneRow laneRow--empty"
          role="status"
          @pointerdown="onEmptyLanePointerDown"
        >
          <span class="laneEmpty">{{ store.tUi('timelineTracks.emptyLane') }}</span>
        </div>

        <div class="playheadColumn" :style="{ left: playheadLeftPct }">
          <div class="playheadLine" aria-hidden="true" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.timelineRoot {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  min-width: 0;
  min-height: auto;
  height: auto;
  outline: none;
  overflow: visible;
}

.timelineRoot:focus-visible {
  box-shadow: inset 0 0 0 2px var(--im-focus-ring);
}

.rulerSticky {
  position: sticky;
  top: 0;
  z-index: 4;
  background: var(--im-surface);
}

.rulerScrollHost,
.trackScrollHost {
  flex: 0 0 auto;
  width: 100%;
  min-width: 0;
  min-height: auto;
  box-sizing: border-box;
  /**
   * 水平留白在滚动盒上：`trackWide` 无 padding，`left:0%` 与 0s 刻度同一几何原点；
   * 圆钮 `translateX(-50%)` 画入左右 padding 区，不被裁切。
   */
  padding-inline: var(--im-timeline-track-gutter-x, 12px);
  /** 竖直方向必须可见，否则播放头可能被裁切 */
  overflow-x: auto;
  touch-action: pan-y;
  scrollbar-width: thin;
}

.rulerScrollHost {
  overflow-y: hidden;
  scrollbar-width: none;
}

.rulerScrollHost::-webkit-scrollbar {
  display: none;
}

.trackScrollHost {
  overflow-y: hidden;
}

.rulerWide,
.trackWide {
  position: relative;
  box-sizing: border-box;
  min-width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.laneStack {
  display: flex;
  flex-direction: column;
  gap: var(--im-timeline-lane-gap, 0.22rem);
  flex: 0 0 auto;
}

.rulerRow {
  position: relative;
  flex: 0 0 auto;
  height: var(--im-timeline-lane-h, 2.35rem);
  min-height: var(--im-timeline-lane-h, 2.35rem);
  max-height: var(--im-timeline-lane-h, 2.35rem);
  padding: 0.15rem 0 0.25rem;
  border-bottom: 1px solid var(--im-border);
  box-sizing: border-box;
  cursor: pointer;
  background: var(--im-surface);
}

.playheadHeader {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  transform: translateX(-50%);
  z-index: 5;
  pointer-events: none;
}

.playheadHeaderStem {
  position: absolute;
  top: 0.95rem;
  left: 50%;
  bottom: 0;
  width: 2px;
  transform: translateX(-50%);
  margin-left: -1px;
  background: var(--im-accent);
  opacity: 0.88;
  border-radius: 1px;
  pointer-events: none;
}

.ticks {
  position: absolute;
  left: 0;
  right: 0;
  top: 0.15rem;
  height: 1rem;
  pointer-events: none;
}

.tick {
  position: absolute;
  transform: translateX(-50%);
  top: 0;
  width: 1px;
  pointer-events: none;
}

.tick--minor {
  height: 0.35rem;
  background: var(--im-border-2);
}

.tick--major {
  height: 0.55rem;
  background: var(--im-border-strong);
}

.lbl {
  position: absolute;
  left: 50%;
  top: 0.65rem;
  transform: translateX(-50%);
  font-size: 0.625rem;
  color: var(--im-muted-2);
  white-space: nowrap;
}

.laneRow {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  height: var(--im-timeline-lane-h, 2.35rem);
  min-height: var(--im-timeline-lane-h, 2.35rem);
  max-height: var(--im-timeline-lane-h, 2.35rem);
  border-radius: 8px;
  box-sizing: border-box;
  transition: background-color 0.12s ease;
}

.laneRow--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--im-timeline-lane-h, 2.35rem);
  min-height: var(--im-timeline-lane-h, 2.35rem);
  max-height: var(--im-timeline-lane-h, 2.35rem);
  cursor: pointer;
}

.laneEmpty {
  font-size: 0.75rem;
  color: var(--im-muted);
  padding: 0 0.25rem;
}

.laneInner {
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  min-height: 2rem;
  /** 去掉纵向 margin，避免 `laneRow` 实际高度大于 `--im-timeline-lane-h` 与左侧 `layerRow` 错位 */
  margin: 0;
  border-radius: 6px;
  background: transparent;
}

.laneRow.selected {
  background: color-mix(in srgb, var(--im-selection-bg) 55%, transparent);
}

.playheadColumn {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  transform: translateX(-50%);
  z-index: 4;
  pointer-events: none;
}

.playheadKnob {
  pointer-events: auto;
  position: absolute;
  left: 50%;
  top: 0.2rem;
  transform: translate(-50%, 0);
  width: 12px;
  height: 12px;
  border-radius: 999px;
  border: 2px solid var(--im-surface);
  padding: 0;
  background: var(--im-accent);
  box-shadow: 0 0 0 1px var(--im-focus-ring);
  cursor: grab;
}

.playheadKnob:active {
  cursor: grabbing;
}

.playheadKnob:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 3px;
}

.playheadLine {
  position: absolute;
  top: 0;
  left: 50%;
  width: 2px;
  bottom: 0;
  transform: translateX(-50%);
  margin-left: -1px;
  background: var(--im-accent);
  opacity: 0.88;
  border-radius: 1px;
  pointer-events: none;
}

.visibilityWindow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 34px;
  min-width: 12px;
  box-sizing: border-box;
  border: 1px solid color-mix(in srgb, var(--im-accent) 30%, var(--im-border-strong));
  border-radius: 8px;
  background: color-mix(in srgb, var(--im-accent) 14%, var(--im-surface));
  box-shadow:
    inset 0 0 0 1px color-mix(in srgb, var(--im-surface) 60%, transparent),
    0 1px 2px color-mix(in srgb, #000 12%, transparent);
  display: flex;
  align-items: center;
  cursor: ew-resize;
  z-index: 1;
}

.visibilityBody {
  flex: 1;
  height: 100%;
}

.visibilityHandle {
  width: 10px;
  height: 34px;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--im-accent) 52%, var(--im-border-strong));
  background: color-mix(in srgb, var(--im-surface) 74%, #fff);
  padding: 0;
  margin-top: 0;
  cursor: ew-resize;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px color-mix(in srgb, #000 14%, transparent);
}

.visibilityHandle--start {
  margin-left: -5px;
}

.visibilityHandle--end {
  margin-right: -5px;
}

.visibilityHandleGrip {
  width: 4px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, #ffffff 72%, transparent);
  background:
    repeating-linear-gradient(
      180deg,
      color-mix(in srgb, var(--im-surface) 82%, #ffffff) 0 2px,
      color-mix(in srgb, var(--im-accent) 24%, var(--im-surface)) 2px 4px
    );
  box-shadow:
    inset 0 1px 0 color-mix(in srgb, #ffffff 70%, transparent),
    0 0 0 1px color-mix(in srgb, var(--im-accent) 18%, transparent);
}

.clipBlock {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 24px;
  min-width: 8px;
  box-sizing: border-box;
  border-radius: 6px;
  border: 1px solid var(--im-accent);
  background: var(--im-timeline-handle);
  display: flex;
  align-items: stretch;
  cursor: grab;
  z-index: 3;
  user-select: none;
}

.clipBlock.selected {
  border-color: var(--im-accent);
  background: var(--im-timeline-handle-hover);
  box-shadow: 0 0 0 2px var(--im-focus-ring);
}

.clipBlock:active {
  cursor: grabbing;
}

.clipBody {
  flex: 1;
  position: relative;
  min-width: 0;
}

.clipKeyframes {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.handle {
  position: relative;
  flex: 0 0 auto;
  width: 12px;
  border: 0;
  padding: 0;
  cursor: ew-resize;
  background: var(--im-timeline-handle);
  z-index: 4;
}

.handle::before {
  content: '';
  position: absolute;
  top: -3px;
  bottom: -3px;
}

.handle:hover {
  background: var(--im-timeline-handle-hover);
}

.handle--left {
  border-radius: 5px 0 0 5px;
}

.handle--left::before {
  left: -6px;
  right: -2px;
}

.handle--right {
  border-radius: 0 5px 5px 0;
}

.handle--right::before {
  left: -2px;
  right: -6px;
}

.kfInClip {
  position: absolute;
  top: 50%;
  width: 8px;
  height: 8px;
  margin-left: -4px;
  margin-top: -4px;
  padding: 0;
  border: 1px solid var(--im-accent);
  background: var(--im-surface);
  transform: rotate(45deg);
  cursor: pointer;
  z-index: 2;
  pointer-events: auto;
}

.kfInClip:hover {
  background: var(--im-elevated);
}

.kfInClip.selected {
  border-color: var(--im-accent);
  background: var(--im-accent);
  box-shadow: 0 0 0 2px var(--im-focus-ring);
}

</style>
